import "./styles.css";
import type {
  ApiError,
  GameStatsResponse,
  GuessResponse,
  HistogramBucket,
  MetricsSummary,
  PublicCard,
  PublicDailySequence,
  RevealedPrice,
  ReviewComparisonResponse
} from "../shared/types";

type GameStatus = "loading" | "playing" | "revealing" | "revealed" | "failed" | "complete" | "error";
type Outcome = "pending" | "correct" | "wrong";

interface GameState {
  appId: string;
  instanceId: string;
  daily: PublicDailySequence | null;
  comparisonIndex: number;
  outcomes: Outcome[];
  status: GameStatus;
  busy: boolean;
  advancing: boolean;
  reveal: GuessResponse | null;
  reviewReveals: Record<number, ReviewComparisonResponse>;
  revealedPrices: Record<string, RevealedPrice>;
  countingPriceIds: Set<string>;
  metrics: MetricsSummary | null;
  error: string | null;
  shareCopied: boolean;
  practiceMode: boolean;
}

interface PersistedGameState {
  version: 1;
  gameId: string;
  instanceId: string;
  cardIds: string[];
  savedAt: string;
  comparisonIndex: number;
  outcomes: Outcome[];
  status: GameStatus;
  reveal: GuessResponse | null;
  reviewReveals: Record<number, ReviewComparisonResponse>;
  revealedPrices: Record<string, RevealedPrice>;
  practiceMode: boolean;
}

interface TransitionBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

const APP_ID_KEY = "app_id";
const INSTANCE_ID_KEY = "instance_id";
const APP_STATE_KEY_PREFIX = "prize_check_game_state:";
const AUTO_ADVANCE_MS = 950;
const PAIR_TRANSITION_MS = 430;
const MAX_COUNT_DURATION_MS = 1500;
const MIN_COUNT_DURATION_MS = 250;
const PRICE_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2
});

const rootElement = document.querySelector<HTMLDivElement>("#app");

if (!rootElement) {
  throw new Error("App root was not found.");
}

const root = rootElement;

const state: GameState = {
  appId: getOrCreateAppId(),
  instanceId: getOrCreateInstanceId(),
  daily: null,
  comparisonIndex: 0,
  outcomes: [],
  status: "loading",
  busy: false,
  advancing: false,
  reveal: null,
  reviewReveals: {},
  revealedPrices: {},
  countingPriceIds: new Set(),
  metrics: null,
  error: null,
  shareCopied: false,
  practiceMode: false
};

let autoAdvanceTimer: number | undefined;
let slideTimer: number | undefined;
let revealCompletionTimer: number | undefined;
let confettiTimer: number | undefined;
let shareCopiedTimer: number | undefined;
let transitionCleanup: (() => void) | undefined;
const countFrameIds = new Set<number>();

root.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const cardButton = target.closest("[data-card-id]") as HTMLElement | null;
  const reviewButton = target.closest("[data-review-index]") as HTMLElement | null;
  const actionButton = target.closest("[data-action]") as HTMLElement | null;

  if (cardButton?.dataset.cardId) {
    void chooseCard(cardButton.dataset.cardId);
    return;
  }

  if (reviewButton?.dataset.reviewIndex) {
    void reviewComparison(Number(reviewButton.dataset.reviewIndex));
    return;
  }

  if (!actionButton?.dataset.action) {
    return;
  }

  if (actionButton.dataset.action === "restart") {
    void restartRun();
  }

  if (actionButton.dataset.action === "share") {
    void shareResult();
  }

  if (actionButton.dataset.action === "continue-practice") {
    continuePracticeRun();
  }
});

void init();

async function init(): Promise<void> {
  render();

  try {
    const response = await fetch(gameRequestPath());

    if (!response.ok) {
      throw new Error(`Daily sequence failed with ${response.status}`);
    }

    const daily = (await response.json()) as PublicDailySequence;
    const restored = restorePersistedState(daily);

    if (!restored) {
      startFreshGame(daily);
      persistAppState();
    }
  } catch (error) {
    state.status = "error";
    state.error = error instanceof Error ? error.message : "Unable to load the daily sequence.";
  }

  render();
  scheduleRestoredTimers();

  if (state.status === "failed" || state.status === "complete") {
    void refreshGameStats();
  }
}

function gameRequestPath(): string {
  const requestedGameId = new URLSearchParams(window.location.search).get("game");

  if (!requestedGameId) {
    return "/api/game";
  }

  return `/api/game?gameId=${encodeURIComponent(requestedGameId)}`;
}

async function chooseCard(cardId: string): Promise<void> {
  if (!state.daily || state.status !== "playing" || state.busy || state.advancing) {
    return;
  }

  if (state.practiceMode) {
    await choosePracticeCard(cardId);
    return;
  }

  clearTimers();
  state.busy = true;
  render();

  try {
    const response = await fetch("/api/guess", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        appId: state.appId,
        instanceId: state.instanceId,
        gameId: state.daily.gameId,
        sequenceId: state.daily.sequenceId,
        date: state.daily.date,
        comparisonIndex: state.comparisonIndex,
        selectedCardId: cardId
      })
    });

    const payload = (await response.json()) as GuessResponse | ApiError;

    if (!response.ok || isApiError(payload)) {
      throw new Error(isApiError(payload) ? payload.error : "Guess failed.");
    }

    revealGuessResult(payload);
  } catch (error) {
    state.busy = false;
    state.status = "error";
    state.error = error instanceof Error ? error.message : "Unable to submit that guess.";
    render();
  }
}

async function choosePracticeCard(cardId: string): Promise<void> {
  if (!state.daily) {
    return;
  }

  clearTimers();
  state.busy = true;
  render();

  try {
    const params = new URLSearchParams({
      gameId: state.daily.gameId,
      sequenceId: state.daily.sequenceId,
      date: state.daily.date,
      comparisonIndex: String(state.comparisonIndex)
    });
    const response = await fetch(`/api/review?${params}`);
    const payload = (await response.json()) as ReviewComparisonResponse | ApiError;

    if (!response.ok || isApiError(payload)) {
      throw new Error(isApiError(payload) ? payload.error : "Review failed.");
    }

    revealGuessResult(practiceGuessFromReview(payload, cardId));
  } catch (error) {
    state.busy = false;
    state.status = "error";
    state.error = error instanceof Error ? error.message : "Unable to continue that run.";
    render();
  }
}

function revealGuessResult(result: GuessResponse): void {
  state.countingPriceIds = new Set(
    result.prices
      .filter((price) => state.revealedPrices[price.cardId] === undefined)
      .map((price) => price.cardId)
  );

  for (const price of result.prices) {
    state.revealedPrices[price.cardId] = price;
  }

  state.reveal = result;
  state.reviewReveals[result.comparisonIndex] = reviewFromGuess(result);
  state.metrics = state.practiceMode ? state.metrics : null;
  state.busy = false;
  state.status = "revealing";
  persistAppState();
  render();
  startCountUpAnimations(result);
}

function practiceGuessFromReview(review: ReviewComparisonResponse, selectedCardId: string): GuessResponse {
  const correct = selectedCardId === review.winnerCardId;
  const comparisonCount = state.daily?.comparisonCount ?? 10;
  const correctCount = state.outcomes.filter((outcome) => outcome === "correct").length + (correct ? 1 : 0);

  return {
    gameId: review.gameId,
    correct,
    completed: review.comparisonIndex >= comparisonCount - 1,
    comparisonIndex: review.comparisonIndex,
    correctCount,
    selectedCardId,
    winnerCardId: review.winnerCardId,
    prices: review.prices,
    metrics: null
  };
}

function advancePair(): void {
  if (!state.daily || state.status !== "revealed" || state.advancing) {
    return;
  }

  clearTimers();
  const nextIndex = state.comparisonIndex + 1;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !startPairTransition(state.comparisonIndex, nextIndex)) {
    completePairAdvance(nextIndex);
  }
}

function continuePracticeRun(): void {
  if (!state.daily || state.status !== "failed" || state.advancing) {
    return;
  }

  const failedIndex = recordedFailureIndex();
  const nextIndex = failedIndex === null ? state.comparisonIndex + 1 : failedIndex + 1;

  if (nextIndex >= state.daily.comparisonCount) {
    return;
  }

  clearTimers();
  state.practiceMode = true;
  state.shareCopied = false;

  if (
    failedIndex === null ||
    state.comparisonIndex !== failedIndex ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    !startPairTransition(failedIndex, nextIndex)
  ) {
    completePairAdvance(nextIndex);
  }
}

async function restartRun(): Promise<void> {
  clearTimers();
  state.instanceId = createInstanceId();
  setStoredValue(INSTANCE_ID_KEY, state.instanceId);
  state.comparisonIndex = 0;
  state.outcomes = Array.from({ length: state.daily?.comparisonCount ?? 10 }, () => "pending");
  state.status = state.daily ? "playing" : "loading";
  state.busy = false;
  state.advancing = false;
  state.reveal = null;
  state.reviewReveals = {};
  state.revealedPrices = {};
  state.countingPriceIds = new Set();
  state.metrics = null;
  state.error = null;
  state.practiceMode = false;
  persistAppState();
  render();

  if (!state.daily) {
    await init();
  }
}

async function shareResult(): Promise<void> {
  const shareUrl = new URL(window.location.href);

  if (state.daily?.gameId) {
    shareUrl.searchParams.set("game", state.daily.gameId);
  }

  await copyTextToClipboard(shareText(shareUrl));
  showShareCopied();
}

function shareText(shareUrl: URL): string {
  const comparisonCount = state.daily?.comparisonCount ?? 10;
  const correctCount = rankedCorrectCount();
  const checks = "✅".repeat(correctCount);
  const perfect = correctCount === comparisonCount;
  const resultLine = `${perfect ? "🎉 " : ""}Prize Check: ${correctCount}/${comparisonCount}`;
  const titleLine = state.daily?.title ?? "Daily challenge";
  const checkLine = checks ? `${checks}\n` : "";

  return `${resultLine}\n${titleLine}\n${checkLine}${shareUrl.toString()}`;
}

function showShareCopied(): void {
  if (shareCopiedTimer) {
    window.clearTimeout(shareCopiedTimer);
  }

  state.shareCopied = true;
  render();

  shareCopiedTimer = window.setTimeout(() => {
    state.shareCopied = false;
    shareCopiedTimer = undefined;
    render();
  }, 5000);
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function currentDailyUrl(): string {
  const url = new URL(window.location.href);

  for (const param of ["game", "gameId", "sequenceId", "date"]) {
    url.searchParams.delete(param);
  }

  return url.toString();
}

async function reviewComparison(index: number): Promise<void> {
  if (!state.daily || state.status !== "failed") {
    return;
  }

  if (!Number.isInteger(index) || index < 0 || index >= state.daily.comparisonCount) {
    return;
  }

  clearTimers();
  state.comparisonIndex = index;
  persistAppState();
  render();

  if (state.reviewReveals[index]) {
    return;
  }

  try {
    const params = new URLSearchParams({
      gameId: state.daily.gameId,
      sequenceId: state.daily.sequenceId,
      date: state.daily.date,
      comparisonIndex: String(index)
    });
    const response = await fetch(`/api/review?${params}`);
    const payload = (await response.json()) as ReviewComparisonResponse | ApiError;

    if (!response.ok || isApiError(payload)) {
      throw new Error(isApiError(payload) ? payload.error : "Review failed.");
    }

    state.reviewReveals[index] = payload;

    for (const price of payload.prices) {
      state.revealedPrices[price.cardId] = price;
    }

    persistAppState();
    render();
  } catch (error) {
    state.error = error instanceof Error ? error.message : "Unable to load that comparison.";
    render();
  }
}

async function refreshGameStats(): Promise<void> {
  if (!state.daily || (state.status !== "failed" && state.status !== "complete")) {
    return;
  }

  try {
    const params = new URLSearchParams({
      gameId: state.daily.gameId,
      sequenceId: state.daily.sequenceId,
      appId: state.appId,
      instanceId: state.instanceId
    });
    const response = await fetch(`/api/stats?${params}`);
    const payload = (await response.json()) as GameStatsResponse | ApiError;

    if (!response.ok || isApiError(payload)) {
      return;
    }

    state.metrics = payload;
    persistAppState();
    render();
  } catch {
    // Stats are supplementary; keep the restored run playable if refresh fails.
  }
}

function startPairTransition(fromIndex: number, toIndex: number): boolean {
  const daily = state.daily;
  const stage = root.querySelector<HTMLElement>(".game-stage");

  if (!daily || !stage || toIndex < 0 || toIndex >= daily.comparisonCount) {
    return false;
  }

  const outgoingCard = daily.cards[fromIndex];
  const sharedCard = daily.cards[fromIndex + 1];
  const incomingCard = daily.cards[toIndex + 1];
  const outgoingElement = cardElement(outgoingCard.id);
  const sharedOldElement = cardElement(sharedCard.id);

  if (!outgoingElement || !sharedOldElement || !incomingCard) {
    return false;
  }

  const outgoingStart = boxFromRect(outgoingElement.getBoundingClientRect());
  const sharedStart = boxFromRect(sharedOldElement.getBoundingClientRect());
  const outgoingClone = cloneTransitionCard(outgoingElement, outgoingStart);

  state.comparisonIndex = toIndex;
  state.reveal = null;
  state.metrics = state.practiceMode ? state.metrics : null;
  state.advancing = true;
  state.status = "playing";
  persistAppState();
  render();

  const transitionStage = root.querySelector<HTMLElement>(".game-stage");
  const sharedElement = cardElement(sharedCard.id);
  const incomingElement = cardElement(incomingCard.id);

  if (!transitionStage || !sharedElement || !incomingElement) {
    state.advancing = false;
    render();
    return true;
  }

  const sharedEnd = boxFromRect(sharedElement.getBoundingClientRect());
  const incomingEnd = boxFromRect(incomingElement.getBoundingClientRect());
  const isVertical = Math.abs(sharedEnd.top - sharedStart.top) > Math.abs(sharedEnd.left - sharedStart.left);
  const sharedClone = cloneTransitionCard(sharedElement, sharedStart);
  const incomingClone = cloneTransitionCard(incomingElement, incomingStartBox(incomingEnd, isVertical));
  const hiddenElements = [sharedElement, incomingElement];
  const animations: Animation[] = [];
  const layer = document.createElement("div");
  let finished = false;

  layer.className = "pair-transition-layer";
  layer.setAttribute("aria-hidden", "true");
  layer.appendChild(outgoingClone);
  layer.appendChild(sharedClone);
  layer.appendChild(incomingClone);
  transitionStage.appendChild(layer);

  for (const element of hiddenElements) {
    element.style.visibility = "hidden";
  }

  animations.push(animateBox(outgoingClone, outgoingStart, outgoingEndBox(outgoingStart, isVertical), 0));
  animations.push(animateBox(sharedClone, sharedStart, sharedEnd, 1));
  animations.push(animateBox(incomingClone, incomingStartBox(incomingEnd, isVertical), incomingEnd, 1));

  const finish = () => {
    if (finished) {
      return;
    }

    finished = true;

    if (slideTimer) {
      window.clearTimeout(slideTimer);
      slideTimer = undefined;
    }

    transitionCleanup = undefined;
    state.advancing = false;
    render();
    layer.remove();
  };

  transitionCleanup = () => {
    if (finished) {
      return;
    }

    finished = true;
    animations.forEach((animation) => animation.cancel());
    layer.remove();
    transitionCleanup = undefined;
    state.advancing = false;
  };

  slideTimer = window.setTimeout(finish, PAIR_TRANSITION_MS + 80);
  void Promise.allSettled(animations.map((animation) => animation.finished)).then(finish);

  return true;
}

function completePairAdvance(toIndex: number): void {
  state.comparisonIndex = toIndex;
  state.reveal = null;
  state.metrics = state.practiceMode ? state.metrics : null;
  state.advancing = false;
  state.status = "playing";
  persistAppState();
  render();
}

function render(): void {
  root.innerHTML = `
    <main class="app-shell ${shellModeClass()}">
      ${renderHeader()}
      ${renderBody()}
    </main>
  `;
}

function shellModeClass(): string {
  return state.status === "failed" || state.status === "complete" ? "is-results-mode" : "is-play-mode";
}

function renderHeader(): string {
  const daily = state.daily;
  const round = Math.min(state.comparisonIndex + 1, daily?.comparisonCount ?? 10);
  const total = daily?.comparisonCount ?? 10;
  const streak = state.practiceMode ? state.outcomes.filter((outcome) => outcome === "correct").length : currentCorrectCount();
  const updated = daily ? formatUpdatedDate(daily.priceUpdated) : "Loading";

  return `
    <header class="topbar">
      <div class="brand">
        <span class="brand-mark" aria-hidden="true"></span>
        <span>Prize Check</span>
      </div>
      <div class="topbar-stats" aria-label="Run status">
        <span class="stat-pill">Round <strong>${round}</strong>/<span>${total}</span></span>
        <span class="stat-pill">Streak <strong>${streak}</strong></span>
      </div>
      <div class="updated">Prices updated ${escapeHtml(updated)}</div>
    </header>
  `;
}

function renderBody(): string {
  if (state.status === "loading") {
    return `<section class="state-panel"><div class="loader"></div><p>Loading daily sequence</p></section>`;
  }

  if (state.status === "error") {
    return `
      <section class="state-panel state-panel-error">
        <h1>Could not load the game</h1>
        <p>${escapeHtml(state.error ?? "Something went wrong.")}</p>
        <button class="primary-button" data-action="restart">Retry</button>
      </section>
    `;
  }

  if (!state.daily) {
    return "";
  }

  const cards = activeCards();
  const showAnalytics = state.status === "failed" || state.status === "complete";

  return `
    <section class="game-layout ${showAnalytics ? "has-results" : ""}">
      <div class="play-surface">
        ${renderProgress()}
        ${renderPrompt()}
        <div class="game-stage">
          ${renderCorrectPopover()}
          ${renderCard(cards.left, "left")}
          <div class="versus" aria-hidden="true">VS</div>
          ${renderCard(cards.right, "right")}
        </div>
        ${renderRevealFooter()}
      </div>
      ${showAnalytics ? renderAnalytics() : ""}
    </section>
  `;
}

function renderProgress(): string {
  const daily = state.daily;

  if (!daily) {
    return "";
  }

  return `
    <div class="progress-rail" aria-label="Daily progress">
      ${Array.from({ length: daily.comparisonCount }, (_, index) => {
          const stateClass = progressClass(index);
          const isActive = stateClass.split(" ").includes("active");
          const label = `Review comparison ${index + 1}`;
          const dot =
            state.status === "failed"
              ? `<button class="progress-dot" type="button" data-review-index="${index}" aria-label="${label}" ${isActive ? 'aria-current="step"' : ""}></button>`
              : `<span class="progress-dot" aria-hidden="true"></span>`;
          return `
            <div class="progress-node ${stateClass}">
              ${dot}
            </div>
          `;
        })
        .join("")}
    </div>
  `;
}

function renderPrompt(): string {
  if (!state.daily) {
    return "";
  }

  if (state.status === "failed" && state.reveal && state.comparisonIndex === state.reveal.comparisonIndex) {
    const selected = cardById(state.reveal.selectedCardId);
    const winner = cardById(state.reveal.winnerCardId);

    return `
      <div class="prompt result-prompt wrong-prompt">
        <span class="rule"></span>
        <h1>You chose ${escapeHtml(selected?.name ?? "that card")}. ${escapeHtml(winner?.name ?? "The other card")} is worth more.</h1>
        <span class="rule"></span>
      </div>
    `;
  }

  if (state.status === "failed") {
    return `
      <div class="prompt result-prompt">
        <span class="rule"></span>
        <h1>Review comparison ${state.comparisonIndex + 1}</h1>
        <span class="rule"></span>
      </div>
    `;
  }

  if (state.status === "revealing") {
    return `
      <div class="prompt result-prompt">
        <span class="rule"></span>
        <h1>Revealing prices</h1>
        <span class="rule"></span>
      </div>
    `;
  }

  if (state.status === "complete") {
    return `
      <div class="prompt result-prompt correct-prompt">
        <span class="rule"></span>
        <h1>${state.practiceMode ? "Practice complete" : "Perfect run"}</h1>
        <span class="rule"></span>
      </div>
    `;
  }

  if (state.status === "revealed" && state.reveal) {
    const resultText = state.reveal.correct ? "Correct" : "Not quite";
    const resultClass = state.reveal.correct ? "correct-prompt" : "wrong-prompt";

    return `
      <div class="prompt result-prompt ${resultClass}">
        <span class="rule"></span>
        <h1>${resultText}</h1>
        <span class="rule"></span>
      </div>
    `;
  }

  return `
    <div class="prompt">
      <span class="rule"></span>
      <h1>Tap the card worth more</h1>
      <span class="rule"></span>
    </div>
  `;
}

function renderCorrectPopover(): string {
  if (state.status !== "revealed" || !state.reveal) {
    return "";
  }

  const winner = cardById(state.reveal.winnerCardId);

  return `
    <div class="result-popover" role="status">
      <span>${escapeHtml(winner?.name ?? "That card")} is worth more.</span>
    </div>
  `;
}

function comparisonRevealFor(index: number): ReviewComparisonResponse | null {
  if (state.reviewReveals[index]) {
    return state.reviewReveals[index];
  }

  if (state.reveal?.comparisonIndex === index) {
    return reviewFromGuess(state.reveal);
  }

  return null;
}

function renderCard(card: PublicCard, side: "left" | "right"): string {
  const displayReveal = comparisonRevealFor(state.comparisonIndex);
  const activeRevealPrice = displayReveal?.prices.find((price) => price.cardId === card.id);
  const revealPrice = activeRevealPrice ?? state.revealedPrices[card.id];
  const outcomeVisible = state.status !== "revealing";
  const wrongGuessVisible =
    (state.status === "failed" || state.status === "revealed") &&
    state.reveal?.comparisonIndex === state.comparisonIndex &&
    !state.reveal.correct;
  const countUp = state.status === "revealing" && activeRevealPrice && state.countingPriceIds.has(card.id);
  const isWinner = outcomeVisible && displayReveal?.winnerCardId === card.id;
  const isSelected = outcomeVisible && wrongGuessVisible && state.reveal?.selectedCardId === card.id;
  const isWrongPick = isSelected && !isWinner;
  const disabled = state.status !== "playing" || state.busy || state.advancing;
  const label = `Choose ${card.name}`;
  const resultLabel = isWinner ? "Worth more" : isWrongPick ? "Your pick" : "";

  return `
    <button class="card-shell ${side} ${isWinner ? "is-winner" : ""} ${isWrongPick ? "is-wrong" : ""}" 
      data-card-id="${card.id}" 
      type="button"
      aria-label="${escapeHtml(label)}"
      ${disabled ? "disabled" : ""}>
      ${resultLabel ? `<span class="result-label">${escapeHtml(resultLabel)}</span>` : ""}
      <div class="card-image-wrap">
        <img src="${card.imageUrl}" alt="${escapeHtml(card.name)} card" loading="eager" />
      </div>
      <div class="card-copy">
        <div>
          <h2>${escapeHtml(card.name)}</h2>
          <p>${escapeHtml(card.set)}</p>
        </div>
        <div class="chips">
          <span>${card.year}</span>
          <span>${escapeHtml(card.rarity)}</span>
          <span>${escapeHtml(card.number)}</span>
          <span>${escapeHtml(card.finish)}</span>
        </div>
        <div class="price-block">
          <span class="price-label">TCG market</span>
          ${
            revealPrice
              ? renderPrice(revealPrice, {
                  countUp: Boolean(countUp),
                  isWinner: Boolean(isWinner),
                  isWrongPick: Boolean(isWrongPick)
                })
              : `<span class="hidden-price"><span class="lock-icon" aria-hidden="true"></span>Hidden</span>`
          }
        </div>
      </div>
    </button>
  `;
}

function renderPrice(
  revealPrice: RevealedPrice,
  options: { countUp: boolean; isWinner: boolean; isWrongPick: boolean }
): string {
  const className = `price ${options.isWinner ? "winner-price" : options.isWrongPick ? "wrong-price" : ""}`;

  if (!options.countUp) {
    return `<strong class="${className}">${escapeHtml(revealPrice.formatted)}</strong>`;
  }

  return `
    <strong
      class="${className}"
      data-count-up="true"
      data-count-target="${revealPrice.marketPrice}"
      data-count-duration="${countDurationForPrice(revealPrice)}"
    >${PRICE_FORMATTER.format(0)}</strong>
  `;
}

function renderRevealFooter(): string {
  if (!state.daily) {
    return "";
  }

  if (state.status === "failed" || state.status === "complete") {
    const copyLabel = state.shareCopied ? "Copied to clipboard" : "Copy link";
    const practiceButton =
      state.status === "failed" && canContinuePractice()
        ? `<button class="primary-button" type="button" data-action="continue-practice">Play out game</button>`
        : "";

    return `
      <div class="reveal-footer result-actions">
        ${practiceButton}
        <button class="secondary-button" type="button" data-action="share">${copyLabel}</button>
      </div>
    `;
  }

  return "";
}

function canContinuePractice(): boolean {
  const failedIndex = recordedFailureIndex();
  return Boolean(state.daily && state.status === "failed" && failedIndex !== null && failedIndex + 1 < state.daily.comparisonCount);
}

function renderAnalytics(): string {
  const metrics = state.metrics;
  const correctCount = metrics?.correctCount ?? rankedCorrectCount();
  const failedComparison = metrics?.failedComparison;
  const underestimatedCard = underestimatedFailedCard();

  return `
    <aside class="analytics-panel">
      <h2>Today's results</h2>
      <div class="result-stack">
        <div class="result-row">
          <span class="mini-icon success"></span>
          <span>You made it</span>
          <strong>${correctCount} correct</strong>
        </div>
        <div class="result-row">
          <span class="mini-icon ${failedComparison ? "danger" : "success"}"></span>
          <span>${failedComparison ? "Failed on" : "Finished"}</span>
          <strong>${
            failedComparison
              ? escapeHtml(underestimatedCard?.name ?? `comparison ${failedComparison}`)
              : `all ${state.daily?.comparisonCount ?? 10}`
          }</strong>
        </div>
        <div class="result-row">
          <span class="mini-icon info"></span>
          <span>Rank</span>
          <strong>Top ${metrics?.topPercent ?? 1}% today</strong>
        </div>
      </div>
      ${renderCurrentDailyNotice()}
      <div class="chart-head">
        <h3>Where players failed</h3>
        <span>${metrics?.totalFinishedRuns ?? 0} runs</span>
      </div>
      ${renderHistogram(metrics?.histogram ?? [])}
    </aside>
  `;
}

function renderCurrentDailyNotice(): string {
  if (!state.daily || state.daily.isDaily || (state.status !== "failed" && state.status !== "complete")) {
    return "";
  }

  return `
    <div class="daily-notice">
      <strong>New daily available</strong>
      <span>Play today's challenge when you're ready.</span>
      <a class="daily-notice-link" href="${escapeHtml(currentDailyUrl())}">Go to daily</a>
    </div>
  `;
}

function underestimatedFailedCard(): PublicCard | null {
  const failedIndex = recordedFailureIndex();

  if (failedIndex === null || failedIndex < 0) {
    return null;
  }

  const failedReveal = comparisonRevealFor(failedIndex);
  const underestimatedCardId =
    failedReveal?.winnerCardId ??
    (state.reveal?.comparisonIndex === failedIndex ? state.reveal.winnerCardId : null);

  return underestimatedCardId ? (cardById(underestimatedCardId) ?? null) : null;
}

function recordedFailureIndex(): number | null {
  const failedComparison = state.metrics?.failedComparison;

  if (failedComparison !== null && failedComparison !== undefined) {
    return failedComparison - 1;
  }

  const firstWrongIndex = state.outcomes.findIndex((outcome) => outcome === "wrong");

  if (firstWrongIndex >= 0) {
    return firstWrongIndex;
  }

  if (state.status === "failed" && state.reveal) {
    return state.reveal.comparisonIndex;
  }

  return null;
}

function renderHistogram(histogram: HistogramBucket[]): string {
  const activeComparison = state.metrics?.failedComparison;
  const buckets =
    histogram.length > 0
      ? histogram
      : Array.from({ length: 10 }, (_, index) => ({
          comparison: index + 1,
          failures: 0,
          percent: 0
        }));
  const max = Math.max(...buckets.map((bucket) => bucket.percent), 1);

  return `
    <div class="histogram" aria-label="Histogram of failed comparisons">
      ${buckets
        .map((bucket) => {
          const height = Math.max(8, Math.round((bucket.percent / max) * 100));
          const active = bucket.comparison === activeComparison;
          return `
            <div class="histogram-bar ${active ? "active" : ""}">
              <span class="bar-value">${bucket.percent}%</span>
              <span class="bar-fill" style="height: ${height}%"></span>
              <span class="bar-label">${bucket.comparison}</span>
            </div>
          `;
        })
        .join("")}
    </div>
  `;
}

function progressClass(index: number): string {
  const classes: string[] = [];

  if (state.status === "failed" && index === state.comparisonIndex) {
    classes.push("active");
  }

  if (state.outcomes[index] === "correct") {
    classes.push("done");
    return classes.join(" ");
  }

  if (state.outcomes[index] === "wrong") {
    classes.push("failed");
    return classes.join(" ");
  }

  if (index === state.comparisonIndex && state.status !== "complete") {
    classes.push("active");
  }

  return classes.join(" ");
}

function reviewFromGuess(result: GuessResponse): ReviewComparisonResponse {
  return {
    gameId: result.gameId,
    comparisonIndex: result.comparisonIndex,
    winnerCardId: result.winnerCardId,
    prices: result.prices
  };
}

function cardElement(cardId: string): HTMLElement | null {
  return root.querySelector<HTMLElement>(`.card-shell[data-card-id="${cardId}"]`);
}

function boxFromRect(rect: DOMRect): TransitionBox {
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height
  };
}

function cloneTransitionCard(element: HTMLElement, box: TransitionBox): HTMLElement {
  const clone = element.cloneNode(true) as HTMLElement;
  clone.classList.add("pair-transition-card");
  clone.setAttribute("aria-hidden", "true");
  clone.removeAttribute("data-card-id");
  applyTransitionBox(clone, box);
  return clone;
}

function animateBox(element: HTMLElement, from: TransitionBox, to: TransitionBox, endOpacity: number): Animation {
  applyTransitionBox(element, from);
  return element.animate(
    [
      transitionKeyframe(from, 1),
      transitionKeyframe(to, endOpacity)
    ],
    {
      duration: PAIR_TRANSITION_MS,
      easing: "cubic-bezier(0.22, 0.84, 0.28, 1)",
      fill: "forwards"
    }
  );
}

function applyTransitionBox(element: HTMLElement, box: TransitionBox): void {
  element.style.position = "fixed";
  element.style.left = `${box.left}px`;
  element.style.top = `${box.top}px`;
  element.style.width = `${box.width}px`;
  element.style.height = `${box.height}px`;
  element.style.margin = "0";
  element.style.pointerEvents = "none";
}

function transitionKeyframe(box: TransitionBox, opacity: number): Keyframe {
  return {
    left: `${box.left}px`,
    top: `${box.top}px`,
    width: `${box.width}px`,
    height: `${box.height}px`,
    opacity
  };
}

function incomingStartBox(end: TransitionBox, isVertical: boolean): TransitionBox {
  const offset = isVertical ? Math.min(96, end.height * 0.28) : Math.min(112, end.width * 0.28);
  return {
    ...end,
    left: isVertical ? end.left : end.left + offset,
    top: isVertical ? end.top + offset : end.top
  };
}

function outgoingEndBox(start: TransitionBox, isVertical: boolean): TransitionBox {
  const offset = isVertical ? Math.min(96, start.height * 0.28) : Math.min(112, start.width * 0.28);
  return {
    ...start,
    left: isVertical ? start.left : start.left - offset,
    top: isVertical ? start.top - offset : start.top
  };
}

function activeCards(): { left: PublicCard; right: PublicCard } {
  const daily = requireDaily();
  return {
    left: daily.cards[state.comparisonIndex],
    right: daily.cards[state.comparisonIndex + 1]
  };
}

function cardById(cardId: string): PublicCard | undefined {
  return state.daily?.cards.find((card) => card.id === cardId);
}

function currentCorrectCount(): number {
  if (state.status === "failed" && state.reveal) {
    return state.reveal.correctCount;
  }

  if (state.status === "complete") {
    if (state.practiceMode) {
      return state.outcomes.filter((outcome) => outcome === "correct").length;
    }

    return state.daily?.comparisonCount ?? 10;
  }

  return state.outcomes.filter((outcome) => outcome === "correct").length;
}

function rankedCorrectCount(): number {
  if (state.metrics) {
    return state.metrics.correctCount;
  }

  const firstWrongIndex = state.outcomes.findIndex((outcome) => outcome === "wrong");

  if (firstWrongIndex >= 0) {
    return firstWrongIndex;
  }

  return currentCorrectCount();
}

function startCountUpAnimations(result: GuessResponse): void {
  clearCountTimers();

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const priceElements = Array.from(root.querySelectorAll<HTMLElement>("[data-count-up='true']"));
  const maxDuration = Math.max(
    ...priceElements.map((element) => Number(element.dataset.countDuration)).filter(Number.isFinite),
    MIN_COUNT_DURATION_MS
  );

  if (reducedMotion || priceElements.length === 0) {
    for (const element of priceElements) {
      const target = Number(element.dataset.countTarget);
      element.textContent = PRICE_FORMATTER.format(Number.isFinite(target) ? target : 0);
    }
    finishReveal(result);
    return;
  }

  for (const element of priceElements) {
    const target = Number(element.dataset.countTarget);
    const duration = Number(element.dataset.countDuration);

    animatePriceCount(
      element,
      Number.isFinite(target) ? target : 0,
      Number.isFinite(duration) ? duration : maxDuration
    );
  }

  revealCompletionTimer = window.setTimeout(() => finishReveal(result), maxDuration + 80);
}

function animatePriceCount(element: HTMLElement, target: number, duration: number): void {
  const start = performance.now();

  const tick = (now: number) => {
    const elapsed = now - start;
    const progress = Math.min(1, elapsed / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    element.textContent = PRICE_FORMATTER.format(target * eased);

    if (progress < 1) {
      const frameId = window.requestAnimationFrame(tick);
      countFrameIds.add(frameId);
    } else {
      element.textContent = PRICE_FORMATTER.format(target);
    }
  };

  const frameId = window.requestAnimationFrame(tick);
  countFrameIds.add(frameId);
}

function finishReveal(result: GuessResponse): void {
  clearCountTimers();

  if (
    !state.reveal ||
    state.reveal.comparisonIndex !== result.comparisonIndex ||
    state.reveal.selectedCardId !== result.selectedCardId ||
    state.status !== "revealing"
  ) {
    return;
  }

  const existingMetrics = state.metrics;
  state.metrics = state.practiceMode ? existingMetrics : result.metrics;
  state.countingPriceIds = new Set();
  state.outcomes[state.comparisonIndex] = result.correct ? "correct" : "wrong";

  if (state.practiceMode) {
    state.status = result.completed ? "complete" : "revealed";
    persistAppState();
    render();

    if (result.correct) {
      launchConfetti();
    }

    if (!result.completed) {
      autoAdvanceTimer = window.setTimeout(advancePair, AUTO_ADVANCE_MS);
    }

    return;
  }

  state.status = result.correct ? (result.completed ? "complete" : "revealed") : "failed";
  persistAppState();
  render();

  if (result.correct) {
    launchConfetti();
  }

  if (result.correct && !result.completed) {
    autoAdvanceTimer = window.setTimeout(advancePair, AUTO_ADVANCE_MS);
  }
}

function countDurationForPrice(price: RevealedPrice): number {
  const prices = state.reveal?.prices.filter((revealed) => state.countingPriceIds.has(revealed.cardId)) ?? [price];
  const normalizedPrices = prices.length > 0 ? prices : [price];
  const maxWeight = Math.max(...normalizedPrices.map((revealed) => priceDurationWeight(revealed.marketPrice)), 0.01);
  const duration = (priceDurationWeight(price.marketPrice) / maxWeight) * MAX_COUNT_DURATION_MS;

  return Math.min(MAX_COUNT_DURATION_MS, Math.max(MIN_COUNT_DURATION_MS, Math.round(duration)));
}

function priceDurationWeight(price: number): number {
  const normalizedPrice = Math.max(0.01, price);
  return 2 ** Math.log10(normalizedPrice);
}

function launchConfetti(): void {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  document.querySelector(".confetti-layer")?.remove();

  const layer = document.createElement("div");
  const colors = ["#28d8ee", "#f3c433", "#48d77b", "#ff614f", "#f4f7f8"];
  layer.className = "confetti-layer";
  layer.setAttribute("aria-hidden", "true");

  for (let index = 0; index < 42; index += 1) {
    const piece = document.createElement("span");
    const fromLeft = index % 2 === 0;
    const distance = 32 + Math.random() * 40;
    const verticalDrift = -120 + Math.random() * 240;
    const rotation = (fromLeft ? 1 : -1) * (420 + Math.random() * 420);

    piece.className = "confetti-piece";
    piece.style.setProperty("--start-x", fromLeft ? "-18px" : "calc(100vw + 18px)");
    piece.style.setProperty("--start-y", `${18 + Math.random() * 58}vh`);
    piece.style.setProperty("--travel-x", `${fromLeft ? distance : -distance}vw`);
    piece.style.setProperty("--travel-y", `${verticalDrift}px`);
    piece.style.setProperty("--rotation", `${rotation}deg`);
    piece.style.setProperty("--duration", `${850 + Math.random() * 650}ms`);
    piece.style.setProperty("--delay", `${Math.random() * 120}ms`);
    piece.style.background = colors[index % colors.length];
    layer.appendChild(piece);
  }

  document.body.appendChild(layer);

  if (confettiTimer) {
    window.clearTimeout(confettiTimer);
  }

  confettiTimer = window.setTimeout(() => {
    layer.remove();
    confettiTimer = undefined;
  }, 1800);
}

function requireDaily(): PublicDailySequence {
  if (!state.daily) {
    throw new Error("Daily sequence is not loaded.");
  }

  return state.daily;
}

function startFreshGame(daily: PublicDailySequence): void {
  state.daily = daily;
  state.comparisonIndex = 0;
  state.outcomes = Array.from({ length: daily.comparisonCount }, () => "pending");
  state.status = "playing";
  state.busy = false;
  state.advancing = false;
  state.reveal = null;
  state.reviewReveals = {};
  state.revealedPrices = {};
  state.countingPriceIds = new Set();
  state.metrics = null;
  state.error = null;
  state.shareCopied = false;
  state.practiceMode = false;
}

function restorePersistedState(daily: PublicDailySequence): boolean {
  const persisted = loadPersistedState(daily.gameId);

  if (!persisted) {
    return false;
  }

  if (!sameCardOrder(persisted.cardIds, daily.cards)) {
    removeStoredValue(appStateKey(daily.gameId));
    return false;
  }

  state.daily = daily;
  state.instanceId = persisted.instanceId || getOrCreateInstanceId();
  setStoredValue(INSTANCE_ID_KEY, state.instanceId);
  state.comparisonIndex = clamp(
    Number.isInteger(persisted.comparisonIndex) ? persisted.comparisonIndex : 0,
    0,
    daily.comparisonCount - 1
  );
  state.outcomes = normalizePersistedOutcomes(persisted.outcomes, daily.comparisonCount);
  state.practiceMode = persisted.practiceMode;
  state.status = normalizePersistedStatus(persisted.status, persisted.reveal, persisted.practiceMode);
  state.busy = false;
  state.advancing = false;
  state.reveal = persisted.reveal;
  state.reviewReveals = persisted.reviewReveals;
  state.revealedPrices = { ...persisted.revealedPrices };
  state.countingPriceIds = new Set();
  state.metrics = null;
  state.error = null;
  state.shareCopied = false;
  hydratePricesFromReveals();
  return true;
}

function loadPersistedState(gameId: string): PersistedGameState | null {
  const raw = getStoredValue(appStateKey(gameId));

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedGameState>;

    if (parsed.version !== 1 || parsed.gameId !== gameId || typeof parsed.instanceId !== "string") {
      return null;
    }

    return {
      version: 1,
      gameId,
      instanceId: parsed.instanceId,
      cardIds: Array.isArray(parsed.cardIds) ? parsed.cardIds.filter((cardId): cardId is string => typeof cardId === "string") : [],
      savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : new Date().toISOString(),
      comparisonIndex: Number(parsed.comparisonIndex),
      outcomes: Array.isArray(parsed.outcomes) ? parsed.outcomes.filter(isOutcome) : [],
      status: isGameStatus(parsed.status) ? parsed.status : "playing",
      reveal: parsed.reveal ?? null,
      reviewReveals: normalizeReviewReveals(parsed.reviewReveals),
      revealedPrices: normalizeRevealedPrices(parsed.revealedPrices),
      practiceMode: parsed.practiceMode === true
    };
  } catch {
    removeStoredValue(appStateKey(gameId));
    return null;
  }
}

function persistAppState(): void {
  if (!state.daily || state.status === "loading" || state.status === "error") {
    return;
  }

  const payload: PersistedGameState = {
    version: 1,
    gameId: state.daily.gameId,
    instanceId: state.instanceId,
    cardIds: state.daily.cards.map((card) => card.id),
    savedAt: new Date().toISOString(),
    comparisonIndex: state.comparisonIndex,
    outcomes: persistedOutcomes(),
    status: persistedStatus(),
    reveal: state.reveal,
    reviewReveals: state.reviewReveals,
    revealedPrices: state.revealedPrices,
    practiceMode: state.practiceMode
  };

  setStoredValue(appStateKey(state.daily.gameId), JSON.stringify(payload));
  setStoredValue(INSTANCE_ID_KEY, state.instanceId);
}

function sameCardOrder(cardIds: string[], cards: PublicCard[]): boolean {
  return cardIds.length === cards.length && cardIds.every((cardId, index) => cardId === cards[index]?.id);
}

function persistedOutcomes(): Outcome[] {
  const outcomes = [...state.outcomes];

  if (state.reveal && (state.status === "revealing" || state.status === "revealed" || state.status === "failed" || state.status === "complete")) {
    outcomes[state.reveal.comparisonIndex] = state.reveal.correct ? "correct" : "wrong";
  }

  return outcomes;
}

function persistedStatus(): GameStatus {
  if (state.status === "revealing" && state.reveal) {
    if (state.practiceMode) {
      return state.reveal.completed ? "complete" : "revealed";
    }

    return finalStatusForReveal(state.reveal);
  }

  if (state.status === "loading" || state.status === "error") {
    return "playing";
  }

  return state.status;
}

function normalizePersistedStatus(status: GameStatus, reveal: GuessResponse | null, practiceMode: boolean): GameStatus {
  if (status === "revealing" && reveal) {
    if (practiceMode) {
      return reveal.completed ? "complete" : "revealed";
    }

    return finalStatusForReveal(reveal);
  }

  if ((status === "revealed" || status === "failed") && !reveal && !practiceMode) {
    return "playing";
  }

  if (status === "loading" || status === "error") {
    return "playing";
  }

  return status;
}

function finalStatusForReveal(reveal: GuessResponse): GameStatus {
  return reveal.correct ? (reveal.completed ? "complete" : "revealed") : "failed";
}

function scheduleRestoredTimers(): void {
  if (state.status === "revealed" && state.reveal && !state.reveal.completed && (state.reveal.correct || state.practiceMode)) {
    autoAdvanceTimer = window.setTimeout(advancePair, AUTO_ADVANCE_MS);
  }
}

function hydratePricesFromReveals(): void {
  if (state.reveal) {
    for (const price of state.reveal.prices) {
      state.revealedPrices[price.cardId] = price;
    }
  }

  for (const reveal of Object.values(state.reviewReveals)) {
    for (const price of reveal.prices) {
      state.revealedPrices[price.cardId] = price;
    }
  }
}

function normalizePersistedOutcomes(outcomes: Outcome[], comparisonCount: number): Outcome[] {
  return Array.from({ length: comparisonCount }, (_, index) => outcomes[index] ?? "pending");
}

function normalizeReviewReveals(value: unknown): Record<number, ReviewComparisonResponse> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([index, reveal]) => Number.isInteger(Number(index)) && Boolean(reveal))
      .map(([index, reveal]) => [Number(index), reveal as ReviewComparisonResponse])
  );
}

function normalizeRevealedPrices(value: unknown): Record<string, RevealedPrice> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return value as Record<string, RevealedPrice>;
}

function appStateKey(gameId: string): string {
  return `${APP_STATE_KEY_PREFIX}${gameId}`;
}

function isOutcome(value: unknown): value is Outcome {
  return value === "pending" || value === "correct" || value === "wrong";
}

function isGameStatus(value: unknown): value is GameStatus {
  return (
    value === "loading" ||
    value === "playing" ||
    value === "revealing" ||
    value === "revealed" ||
    value === "failed" ||
    value === "complete" ||
    value === "error"
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clearTimers(): void {
  if (autoAdvanceTimer) {
    window.clearTimeout(autoAdvanceTimer);
    autoAdvanceTimer = undefined;
  }

  if (slideTimer) {
    window.clearTimeout(slideTimer);
    slideTimer = undefined;
  }

  if (transitionCleanup) {
    transitionCleanup();
  }

  clearCountTimers();

  if (confettiTimer) {
    window.clearTimeout(confettiTimer);
    confettiTimer = undefined;
  }

  if (shareCopiedTimer) {
    window.clearTimeout(shareCopiedTimer);
    shareCopiedTimer = undefined;
    state.shareCopied = false;
  }

  document.querySelector(".confetti-layer")?.remove();
}

function clearCountTimers(): void {
  if (revealCompletionTimer) {
    window.clearTimeout(revealCompletionTimer);
    revealCompletionTimer = undefined;
  }

  for (const frameId of countFrameIds) {
    window.cancelAnimationFrame(frameId);
  }

  countFrameIds.clear();
}

function getOrCreateAppId(): string {
  const existing = getStoredValue(APP_ID_KEY);

  if (existing) {
    return existing;
  }

  const appId = createAppId();
  setStoredValue(APP_ID_KEY, appId);
  return appId;
}

function getOrCreateInstanceId(): string {
  const existing = getStoredValue(INSTANCE_ID_KEY);

  if (existing) {
    return existing;
  }

  const instanceId = createInstanceId();
  setStoredValue(INSTANCE_ID_KEY, instanceId);
  return instanceId;
}

function createAppId(): string {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function createInstanceId(): string {
  return createAppId();
}

function getStoredValue(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setStoredValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Persistence is best-effort; the game should still work when storage is unavailable.
  }
}

function removeStoredValue(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Persistence is best-effort; the game should still work when storage is unavailable.
  }
}

function formatUpdatedDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) {
    return date;
  }

  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
}

function escapeHtml(value: string | number): string {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function isApiError(payload: unknown): payload is ApiError {
  return typeof payload === "object" && payload !== null && "error" in payload;
}
